// File: src/components/Kiln/KilnSettingsPanel.jsx
// Collect kiln dimensions and auto-estimate litres & usable space units.

import React, { useState } from "react";
import { useKilns } from "../../contexts/KilnContext";
import { estimateUsableSpaceUnitsFromKiln } from "../../ai/estimators/kilnCapacityEstimator";

export default function KilnSettingsPanel() {
  const { kilns, addKiln, updateKiln, deleteKiln, setSelectedKilnId } = useKilns();

  const [form, setForm] = useState({
    name: "",
    type: "Top Loader",
    capacityLitres: "",
    internalWidthCm: "",
    internalDepthCm: "",
    internalHeightCm: "",
    shelfCount: "",
    maxCone: "",
  });

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleAdd() {
    if (!form.name.trim()) return;

    const newKilnBase = {
      name: form.name.trim(),
      type: form.type || "Unknown",
      capacityLitres: form.capacityLitres ? Number(form.capacityLitres) : null,
      internalWidthCm: form.internalWidthCm ? Number(form.internalWidthCm) : null,
      internalDepthCm: form.internalDepthCm ? Number(form.internalDepthCm) : null,
      internalHeightCm: form.internalHeightCm ? Number(form.internalHeightCm) : null,
      shelfCount: form.shelfCount ? Number(form.shelfCount) : null,
      maxCone: form.maxCone ? Number(form.maxCone) : null,
      shelfDiameters: [],
      usableSpaceUnits: null, // will estimate below
    };

    const autoUnits = estimateUsableSpaceUnitsFromKiln(newKilnBase);
    const kilnToSave = {
      ...newKilnBase,
      usableSpaceUnits: autoUnits,
    };

    const id = addKiln(kilnToSave);

    if (setSelectedKilnId && id) {
      setSelectedKilnId(id);
    }

    setForm({
      name: "",
      type: "Top Loader",
      capacityLitres: "",
      internalWidthCm: "",
      internalDepthCm: "",
      internalHeightCm: "",
      shelfCount: "",
      maxCone: "",
    });
  }

  return (
    <div className="p-6 space-y-6 overflow-auto">
      <div>
        <h1 className="text-xl font-semibold">Kilns</h1>
        <p className="text-sm opacity-70 -mt-2">
          Add your kilns with internal dimensions so Mudo can estimate capacity and firing loads.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Add / edit form */}
        <div className="mudo-card p-4 space-y-4">
          <h2 className="mudo-section-title">Add kiln</h2>

          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs opacity-80">Name</label>
              <input
                className="w-full mt-1 rounded-xl bg-[#050608] border border-white/10 px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                placeholder="Kiln A"
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
              />
            </div>

            <div>
              <label className="text-xs opacity-80">Type</label>
              <select
                className="w-full mt-1 rounded-xl bg-[#050608] border border-white/10 px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                value={form.type}
                onChange={(e) => handleChange("type", e.target.value)}
              >
                <option value="Top Loader">Top Loader</option>
                <option value="Front Loader">Front Loader</option>
                <option value="Unknown">Other / Unknown</option>
              </select>
            </div>

            {/* Internal dimensions */}
            <div>
              <label className="text-xs opacity-80">Internal dimensions (cm)</label>
              <div className="mt-1 grid grid-cols-3 gap-2">
                <input
                  className="rounded-xl bg-[#050608] border border-white/10 px-2 py-1.5 text-xs text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  placeholder="Width"
                  value={form.internalWidthCm}
                  onChange={(e) => handleChange("internalWidthCm", e.target.value)}
                />
                <input
                  className="rounded-xl bg-[#050608] border border-white/10 px-2 py-1.5 text-xs text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  placeholder="Depth"
                  value={form.internalDepthCm}
                  onChange={(e) => handleChange("internalDepthCm", e.target.value)}
                />
                <input
                  className="rounded-xl bg-[#050608] border border-white/10 px-2 py-1.5 text-xs text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  placeholder="Height"
                  value={form.internalHeightCm}
                  onChange={(e) => handleChange("internalHeightCm", e.target.value)}
                />
              </div>
              <p className="text-[10px] opacity-60 mt-1">
                Internal chamber size – used to estimate litres and packing capacity.
              </p>
            </div>

            {/* Optional explicit litres */}
            <div>
              <label className="text-xs opacity-80">Capacity (litres, optional)</label>
              <input
                className="w-full mt-1 rounded-xl bg-[#050608] border border-white/10 px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                placeholder="e.g. 120"
                value={form.capacityLitres}
                onChange={(e) => handleChange("capacityLitres", e.target.value)}
              />
              <p className="text-[10px] opacity-60 mt-1">
                Leave blank to calculate from internal dimensions.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs opacity-80">Shelves</label>
                <input
                  className="w-full mt-1 rounded-xl bg-[#050608] border border-white/10 px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  placeholder="e.g. 4"
                  value={form.shelfCount}
                  onChange={(e) => handleChange("shelfCount", e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs opacity-80">Max cone</label>
                <input
                  className="w-full mt-1 rounded-xl bg-[#050608] border border-white/10 px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  placeholder="e.g. 10"
                  value={form.maxCone}
                  onChange={(e) => handleChange("maxCone", e.target.value)}
                />
              </div>
            </div>

            <button
              onClick={handleAdd}
              className="mt-2 text-xs px-3 py-2 rounded-full border border-white/10 bg-black/40 hover:bg-black/60 disabled:opacity-50"
            >
              Save kiln
            </button>
          </div>
        </div>

        {/* Kiln list */}
        <div className="mudo-card p-4 space-y-4 lg:col-span-2">
          <h2 className="mudo-section-title">Your kilns</h2>

          {kilns.length === 0 ? (
            <p className="text-sm opacity-70">
              No kilns yet. Add your first kiln with its internal dimensions on the left.
            </p>
          ) : (
            <div className="space-y-2">
              {kilns.map((k) => {
                const capacityDisplay =
                  k.capacityLitres != null ? `${k.capacityLitres.toFixed(0)} L` : "—";
                const dimsDisplay =
                  k.internalWidthCm && k.internalDepthCm && k.internalHeightCm
                    ? `${k.internalWidthCm} × ${k.internalDepthCm} × ${k.internalHeightCm} cm`
                    : "—";

                return (
                  <div
                    key={k.id}
                    className="border border-white/10 rounded-xl p-3 flex justify-between items-center"
                  >
                    <div className="text-sm">
                      <div className="font-medium">
                        {k.name}{" "}
                        <span className="text-xs opacity-70">
                          ({k.type || "Kiln"})
                        </span>
                      </div>
                      <div className="text-xs opacity-70">
                        Internal: {dimsDisplay}
                      </div>
                      <div className="text-xs opacity-70">
                        Capacity: {capacityDisplay} • Usable:{" "}
                        {k.usableSpaceUnits != null
                          ? `${k.usableSpaceUnits} units`
                          : "auto not set"}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => deleteKiln(k.id)}
                        className="text-[11px] px-2 py-1 rounded-full border border-white/10 bg-black/40"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
